import React, { useContext, useState } from "react";
import { AuthContext } from "../contexts/AuthContext";
import DesktopNavigation from "./DesktopNavigation";
import MobileNavigation from "./MobileNavigation";
import TopBar from "./TopBar";
import { MessageComposerProvider } from "../contexts/MessageComposerContext";
import {
  Bars3Icon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  RectangleGroupIcon,
  UserIcon,
  CogIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

function AppLayout({ children, current }) {
  const { authContext, logout } = useContext(AuthContext);
  const shouldShowNav = authContext.organization.initializationComplete === 1;
  const hasSchedulesAccess = authContext.organization.planFeatures?.Schedules !== false;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build Scheduling nav item based on plan access
  const schedulingNavItem = hasSchedulesAccess
    ? {
        name: "Scheduling",
        icon: CalendarDaysIcon,
        current: current === "scheduling",
        children: [
          {
            name: "Schedules",
            href: "/scheduling/schedules",
            current: current === "schedules",
          },
          {
            name: "Shift templates",
            href: "/scheduling/shift-templates",
            current: current === "shiftTemplates",
          },
        ],
      }
    : {
        name: "Scheduling",
        href: "/scheduling/schedules",
        icon: CalendarDaysIcon,
        current: current === "scheduling" || current === "schedules",
        badge: { text: "PRO", variant: "brand" },
      };

  const navOptions = [
    {
      name: "Dashboard",
      href: "/",
      icon: RectangleGroupIcon,
      current: current === "dashboard",
    },
    schedulingNavItem,
    {
      name: "Messaging",
      href: "/messaging",
      icon: ChatBubbleLeftRightIcon,
      current: current === "messaging",
    },
    {
      name: "Reporting",
      icon: ClipboardDocumentCheckIcon,
      current: current === "reporting",
      children: [
        {
          name: "Activities",
          href: "/reporting/activities",
          current: current === "activities",
        },
        {
          name: "Reports",
          href: "/reporting/reports",
          current: current === "reporting-reports",
        },
      ],
    },
    {
      name: "Customers",
      href: "/customers",
      icon: UserIcon,
      current: current === "customers",
    },
    {
      name: "Sites",
      href: "/sites",
      icon: MapPinIcon,
      current: current === "sites",
    },
    {
      name: "Settings",
      icon: Cog6ToothIcon,
      current: current === "settings",
      separator: true,
      children: [
        {
          name: "Users",
          href: "/settings/users",
          current: current === "users",
        },
        {
          name: "Organization",
          href: "/settings/organization",
          current: current === "organization",
        },
        {
          name: "Billing",
          href: "/billing/subscription",
          current: current === "billing",
        },
      ],
    },
  ];

  if (!shouldShowNav) {
    return (
      <MessageComposerProvider>
        <div className="min-h-screen dark:bg-gray-900">{children}</div>
      </MessageComposerProvider>
    );
  }

  return (
    <MessageComposerProvider>
      <div className="flex flex-col h-screen overflow-hidden dark:bg-gray-900">
        <div className="flex flex-1 min-h-0">
          <aside className="hidden lg:flex lg:flex-col lg:w-64">
            <DesktopNavigation
              navOptions={navOptions}
              // orgName={authContext.organization.orgName}
            />
          </aside>

          <div className="flex flex-col flex-1 min-h-0 bg-gray-50 dark:bg-gray-800">
            <header>
              <TopBar setSidebarOpen={setSidebarOpen} />
            </header>

            <main className="flex-1 min-h-0 overflow-auto flex flex-col bg-white dark:bg-gray-900 rounded-tl-2xl lg:rounded-2xl lg:mr-3 lg:mb-3">
              {children}
            </main>
          </div>
        </div>

        <MobileNavigation
          navOptions={navOptions}
          orgName={authContext.organization.orgName}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
      </div>
    </MessageComposerProvider>
  );
}

export { AppLayout };
